import React from "react"

import LineChart from "shared/ui/Charts/LineChart.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { ConvertedTimeSeriesData } from "types/ReportingDashboard/timeseries.types"

/**
 * Props for TimeSeriesDisplayArea component
 */
export interface TimeSeriesDisplayAreaProps {
  /** Converted time series data */
  timeSeriesChartData: ConvertedTimeSeriesData
  /** Whether in dashboard mode */
  isDashboard: boolean
  /** Selected series for dashboard filtering */
  selectedDashboardSeries: string[]
  /** Selected waves for dashboard filtering */
  selectedDashboardWaves: string[]
  /** Wave variable name for axis label */
  waveVarName: string
  /** Selected statistic type for axis label */
  selectedStatisticType: STATISTIC_TYPE
  /** Chart height */
  lineChartHeight: number
  /** Whether data has been processed */
  hasProcessedData: boolean
  /** Whether currently loading */
  isLoading: boolean
  /** Whether queued for execution */
  isQueuedForExecution: boolean
  /** Whether config is initializing */
  isInitializing: boolean
  /** Whether load has been attempted */
  hasAttemptedLoad: boolean
  /** Error state */
  error: string | null
  /** Y-axis tick formatter */
  formatYTickForLineChart: (value: unknown) => string
  /** Refresh handler */
  onRefresh: () => void
  /** Edit handler */
  onEdit: () => void
}

/**
 * TimeSeriesDisplayArea Component
 *
 * Handles the display area for time series charts.
 * Shows the LineChart component when data is available,
 * or appropriate messages for loading, error, or no data states.
 */
const TimeSeriesDisplayArea: React.FC<TimeSeriesDisplayAreaProps> = ({
  timeSeriesChartData,
  isDashboard,
  selectedDashboardSeries,
  selectedDashboardWaves,
  waveVarName,
  selectedStatisticType,
  lineChartHeight,
  hasProcessedData,
  isLoading,
  isQueuedForExecution,
  isInitializing,
  hasAttemptedLoad,
  error,
  formatYTickForLineChart,
  onRefresh,
  onEdit,
}) => {
  const hasValidChartData =
    timeSeriesChartData.lineChartNivoData.length > 0 &&
    timeSeriesChartData.lineChartNivoData.some(
      (series) => series.data.length > 0
    )

  // Filter data for dashboard mode
  const filteredChartData = timeSeriesChartData.lineChartNivoData
    .filter((series) =>
      isDashboard && selectedDashboardSeries.length > 0
        ? selectedDashboardSeries.includes(series.id)
        : true
    )
    .map((series) => ({
      ...series,
      data: series.data.filter((point) =>
        isDashboard && selectedDashboardWaves.length > 0
          ? selectedDashboardWaves.includes(point.x)
          : true
      ),
    }))

  return (
    <div className="relative flex-grow w-full">
      {/* Valid Chart Data Display */}
      {hasValidChartData && (
        <div
          className="relative flex-grow w-full"
          style={{
            minHeight: "150px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            padding: "10px",
            boxSizing: "border-box",
          }}
        >
          <LineChart
            data={filteredChartData}
            xAxisLabel={waveVarName}
            yAxisLabel={selectedStatisticType}
            height={lineChartHeight}
            formatYTick={formatYTickForLineChart}
          />
        </div>
      )}

      {/* Loading Overlay */}
      {(isLoading || isQueuedForExecution) && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md z-20">
          <div className="text-blue-500 text-sm bg-blue-50 px-3 py-1 rounded-md shadow-sm">
            {isQueuedForExecution ? "Queued for execution..." : "Loading..."}
          </div>
        </div>
      )}

      {/* No Valid Data Message */}
      {hasProcessedData &&
        !hasValidChartData &&
        !isLoading &&
        !isQueuedForExecution &&
        !error &&
        !isInitializing && (
          <div className="flex-grow flex items-center justify-center h-full bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
            <div className="text-center p-4">
              <p className="text-xs text-gray-500 mb-2">
                No valid data to display. This could be due to:
              </p>
              <ul className="text-xs text-gray-400 mb-3 list-disc list-inside">
                <li>All values are zero or identical</li>
                <li>Data doesn't match the selected criteria</li>
                <li>Incompatible variable combinations</li>
              </ul>
              <div className="flex gap-2 justify-center">
                <DataQButton onClick={onRefresh} type="default" size="small">
                  Retry
                </DataQButton>
                <DataQButton onClick={onEdit} type="primary" size="small">
                  Edit Variables
                </DataQButton>
              </div>
            </div>
          </div>
        )}

      {/* Data Needed Message */}
      {!hasProcessedData &&
        timeSeriesChartData.lineChartNivoData.length === 0 &&
        !isLoading &&
        !isQueuedForExecution &&
        !error &&
        !isInitializing &&
        hasAttemptedLoad && (
          <div className="flex-grow flex items-center justify-center h-full bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
            <div className="text-center p-4">
              <p className="text-xs text-gray-500 mb-1">
                Data needed or no data matching criteria.
              </p>
              <DataQButton onClick={onRefresh} type="primary" size="small">
                Retry
              </DataQButton>
            </div>
          </div>
        )}
    </div>
  )
}

export default TimeSeriesDisplayArea
